import { CanvasTexture, SRGBColorSpace } from "three";
import { SURFACE } from "../theme3d";

// Procedural washi-paper-on-wood-lattice surface, baked once into a
// CanvasTexture — the same "cheap 2D canvas decal" pattern already proven
// by src/weapons/BladeShadow.tsx's createShadowBlobTexture(), chosen over a
// custom ShaderMaterial specifically because a new shader program compiling
// mid-session is the exact class of bug that caused the pointLight
// mount/unmount stutter (see memory perf-freeze-jump-fix-2026-07-10) — a
// canvas texture costs one draw call and zero GPU work per frame.
//
// Mirrors design/Diegetic Panel System.dc.html's #washiFill /
// #andonGlow SVG defs: linear paper gradient, a warm radial hotspot in the
// andon's real screen-space direction (the lantern sits lower-left of the
// dōjō, see Dojo.tsx's AndonLantern position), speckled fibre noise, and a
// wood lattice matching Dojo.tsx's ShojiPanel bar spacing.
export interface WashiOptions {
  width: number;
  height: number;
  cols?: number;
  rows?: number;
  maxAnisotropy?: number;
}

const MAX_PX = 1024;
const PX_PER_METER = 384;

// Small, bounded set of distinct (cols, rows, aspect) shapes across the
// whole panel system (menu, story, pause, teach, …) — cached for the app's
// lifetime, no eviction needed (unlike the per-illustration LRU in S5).
const cache = new Map<string, CanvasTexture>();

// Deterministic PRNG (same seed the mockup's feTurbulence used) so the
// fibre speckle pattern doesn't shift between reloads or texture rebuilds.
function mulberry32(seed: number) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function paint(canvas: HTMLCanvasElement, w: number, h: number, cols: number, rows: number) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // Base paper gradient.
  const base = ctx.createLinearGradient(0, 0, w, h);
  base.addColorStop(0, SURFACE.washiTop);
  base.addColorStop(0.5, SURFACE.washiMid);
  base.addColorStop(1, SURFACE.washiBottom);
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, w, h);

  // Andon hotspot — lower-left, matching the lantern's real position in
  // the scene (Dojo.tsx AndonLantern), additive so it warms without
  // washing out the base gradient.
  ctx.globalCompositeOperation = "lighter";
  const andon = ctx.createRadialGradient(
    w * 0.3,
    h * 0.7,
    0,
    w * 0.3,
    h * 0.7,
    Math.max(w, h) * 0.8,
  );
  andon.addColorStop(0, "rgba(224,169,80,0.28)");
  andon.addColorStop(1, "rgba(224,169,80,0)");
  ctx.fillStyle = andon;
  ctx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = "source-over";

  // Washi fibre speckle — deterministic, so the texture is stable across
  // rebuilds instead of re-rolling a different noise pattern each time.
  const rand = mulberry32(7);
  ctx.strokeStyle = SURFACE.fibre;
  ctx.lineWidth = 1;
  const fibreCount = Math.round((w * h) / 620);
  for (let i = 0; i < fibreCount; i++) {
    const x = rand() * w;
    const y = rand() * h;
    const len = 2 + rand() * 6;
    const angle = rand() * Math.PI;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + Math.cos(angle) * len, y + Math.sin(angle) * len);
    ctx.stroke();
  }

  // Wood lattice mullions (mirrors Dojo.tsx ShojiPanel's bar spacing).
  const latticeWidth = Math.max(3, 0.022 * Math.min(w, h));
  ctx.strokeStyle = SURFACE.latticeWood;
  ctx.lineWidth = latticeWidth;
  for (let c = 1; c < cols; c++) {
    const x = (w / cols) * c;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
    ctx.stroke();
  }
  for (let r = 1; r < rows; r++) {
    const y = (h / rows) * r;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
    ctx.stroke();
  }

  // Inner frame line, slightly heavier than the mullions.
  ctx.lineWidth = latticeWidth * 1.25;
  ctx.strokeRect(latticeWidth, latticeWidth, w - latticeWidth * 2, h - latticeWidth * 2);

  // Edge vignette so the panel settles into the room instead of reading as
  // a flat cutout.
  const vignette = ctx.createRadialGradient(
    w / 2,
    h / 2,
    Math.min(w, h) * 0.35,
    w / 2,
    h / 2,
    Math.max(w, h) * 0.75,
  );
  vignette.addColorStop(0, "rgba(0,0,0,0)");
  vignette.addColorStop(1, "rgba(0,0,0,0.35)");
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, w, h);
}

// Must be called lazily inside a component (useMemo), never at module
// scope — `document` isn't guaranteed to exist yet at import time. Same
// discipline as BladeShadow.tsx's createShadowBlobTexture().
export function getWashiTexture({
  width,
  height,
  cols = 4,
  rows = 3,
  maxAnisotropy = 1,
}: WashiOptions): CanvasTexture {
  const key = `${cols}x${rows}@${(width / height).toFixed(2)}`;
  const cached = cache.get(key);
  if (cached) return cached;

  // e.g. the menu panel (2.25m x 1.35m) -> 864x518px, well under the cap;
  // anything larger scales down uniformly to fit the 1024x1024 box.
  const rawW = width * PX_PER_METER;
  const rawH = height * PX_PER_METER;
  const scale = Math.min(1, MAX_PX / Math.max(rawW, rawH));
  const canvasW = Math.max(1, Math.round(rawW * scale));
  const canvasH = Math.max(1, Math.round(rawH * scale));

  const canvas = document.createElement("canvas");
  canvas.width = canvasW;
  canvas.height = canvasH;
  paint(canvas, canvasW, canvasH, cols, rows);

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  texture.anisotropy = maxAnisotropy;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;

  cache.set(key, texture);
  return texture;
}
